import i18n from '@/utils/i18n';
import {
  CatalogCategoryList_GtinFiltersResponseFragment,
  GtinFilterAvailabilityFlags,
  ItemsContext_VariantsPackFieldsFragment,
} from '@/types';
import { CatalogFilter } from '@/enums';
import { ContainmentLevel, GTINMetadata } from '@/contexts/item-context-types';
import { getAllergenTranslation } from '@/utils/gtin-metadata';
import { ListOption } from '@/components/list-item';
import { useDynamicConfigValue } from './use-dynamic-config-value';
import { useMemo, useRef, useState } from 'react';

const extractTypedMetadata = (
  variantPack: ItemsContext_VariantsPackFieldsFragment,
): GTINMetadata | null => {
  const metadata = variantPack.gtin_metadata?.[0]?.metadata;
  return metadata ? (metadata as GTINMetadata) : null;
};

type ValueExtractor = (gtinMetadata: GTINMetadata) => string[];
type BooleanExtractor = (gtinMetadata: GTINMetadata) => boolean;

interface FilterOptions {
  extractor: ValueExtractor;
  includeFilters: boolean;
  key: CatalogFilter;
  label: string;
  remoteOptions?: string[];
  translate?: (value: string) => string;
  vpsWithMetadata: ItemsContext_VariantsPackFieldsFragment[];
}

interface BaseGTINFilter {
  applyStage: () => void;
  filterMatches: (vp: ItemsContext_VariantsPackFieldsFragment) => boolean;
  isAvailable: boolean;
  key: CatalogFilter;
  label: string;
  resetStage: () => void;
  stageValue: () => void;
}

export interface GTINListFilter extends BaseGTINFilter {
  filter: Set<string>;
  options: ListOption[];
  subtitle: string;
  updateFilter: React.Dispatch<React.SetStateAction<Set<string>>>;
}

export interface GTINFlagFilter extends BaseGTINFilter {
  filterApplied: boolean;
  updateFilter: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface GTINFilters {
  applyAllStagedFilters: () => void;
  flagFilters: GTINFlagFilter[];
  listFilters: GTINListFilter[];
  resetAllStagedFilters: () => void;
  resetFilters: () => void;
  stageAllFilters: () => void;
}

const identityTranslation = (value: string) => value;

const useGTINMetadataFilter = ({
  extractor,
  includeFilters,
  key,
  label,
  remoteOptions,
  translate = identityTranslation,
  vpsWithMetadata,
}: FilterOptions) => {
  const [filter, setFilter] = useState<Set<string>>(new Set());
  const stage = useRef<Set<string> | null>(null);

  return useMemo<GTINListFilter>(() => {
    if (!includeFilters) {
      return {
        applyStage: () => {},
        filter: new Set(),
        filterMatches: () => true,
        isAvailable: false,
        key,
        label,
        options: [],
        resetStage: () => {},
        stageValue: () => {},
        subtitle: '',
        updateFilter: () => {},
      };
    }
    const uniqueOptions = remoteOptions
      ? new Set(remoteOptions)
      : vpsWithMetadata.reduce<Set<string>>((acc, vp) => {
          const metadata = extractTypedMetadata(vp);
          if (metadata) {
            const extractedValues = extractor(metadata) || [];
            for (const value of extractedValues) {
              acc.add(value);
            }
          }
          return acc;
        }, new Set());

    const allOptions = Array.from(uniqueOptions).sort(stringSort);

    const listOptions = allOptions.map<ListOption>((item) => ({
      title: translate(item),
      value: String(item),
    }));

    const subtitle =
      filter.size === 0
        ? i18n.t('all')
        : Array.from(filter).map(translate).sort(stringSort).join(', ');

    return {
      applyStage: () => {
        if (stage.current !== null) {
          setFilter(stage.current);
          stage.current = null;
        }
      },
      filter,
      filterMatches: (vp: ItemsContext_VariantsPackFieldsFragment) => {
        if (filter.size === 0) {
          return true;
        }
        const metadata = extractTypedMetadata(vp);
        if (!metadata) {
          return false;
        }
        const extractedValues = extractor(metadata);
        if (extractedValues.length === 0) {
          return false;
        }
        return extractedValues.some((value) => filter.has(value));
      },
      isAvailable: allOptions.length > 0,
      key,
      label,
      options: listOptions,
      resetStage: () => {
        stage.current = null;
      },
      stageValue: () => {
        stage.current = filter;
      },
      subtitle,
      updateFilter: setFilter,
    };
  }, [
    extractor,
    filter,
    includeFilters,
    key,
    label,
    remoteOptions,
    translate,
    vpsWithMetadata,
  ]);
};

interface BooleanFilterOptions {
  extractor: BooleanExtractor;
  includeFilters: boolean;
  isAvailable?: boolean;
  key: CatalogFilter;
  label: string;
  vpsWithMetadata: ItemsContext_VariantsPackFieldsFragment[];
}

const useGTINBooleanFilter = ({
  extractor,
  includeFilters,
  key,
  label,
  vpsWithMetadata,
  isAvailable,
}: BooleanFilterOptions) => {
  const [filterApplied, setFilterApplied] = useState(false);
  const stagedFilter = useRef<boolean | null>(null);

  return useMemo<GTINFlagFilter>(() => {
    if (!includeFilters) {
      return {
        applyStage: () => {},
        filterApplied: false,
        filterMatches: () => true,
        isAvailable: false,
        key,
        label,
        resetStage: () => {},
        stageValue: () => {},
        updateFilter: () => {},
      };
    }
    const itemsHaveFlag =
      isAvailable ??
      vpsWithMetadata.some((vp) => {
        const metadata = extractTypedMetadata(vp);
        if (!metadata) {
          return false;
        }
        return extractor(metadata);
      });

    return {
      applyStage: () => {
        if (stagedFilter.current !== null) {
          setFilterApplied(stagedFilter.current);
          stagedFilter.current = null;
        }
      },
      filterApplied,
      filterMatches: (vp: ItemsContext_VariantsPackFieldsFragment) => {
        if (!filterApplied) {
          return true;
        }
        const metadata = extractTypedMetadata(vp);
        if (!metadata) {
          return false;
        }
        return extractor(metadata);
      },
      isAvailable: itemsHaveFlag,
      key,
      label,
      resetStage: () => {
        stagedFilter.current = null;
      },
      stageValue: () => {
        stagedFilter.current = filterApplied;
      },
      updateFilter: setFilterApplied,
    };
  }, [
    extractor,
    filterApplied,
    key,
    label,
    vpsWithMetadata,
    includeFilters,
    isAvailable,
  ]);
};

const brandExtractor: ValueExtractor = (metadata) => {
  return metadata.brand_name ? [metadata.brand_name] : [];
};
const freeFromExtractor: ValueExtractor = (metadata) => {
  return (metadata.allergens || []).reduce<string[]>((acc, allergen) => {
    if (allergen.containment_level === ContainmentLevel.FreeFrom) {
      acc.push(allergen.name);
    }
    return acc;
  }, []);
};

const originCountryExtractor: ValueExtractor = (metadata) => {
  return metadata.origin_country ? [metadata.origin_country] : [];
};

const packSizeExtractor: ValueExtractor = (metadata) => {
  return metadata.pack_size ? [metadata.pack_size] : [];
};

const categoryExtractor: ValueExtractor = (metadata) => {
  return metadata.category ? [metadata.category] : [];
};

const stringSort = (a: string, b: string) => a.localeCompare(b);

export const useGTINMetadataFilters = (
  vpsWithMetadata: ItemsContext_VariantsPackFieldsFragment[],
  includeFilters: boolean,
  remoteFilters?: CatalogCategoryList_GtinFiltersResponseFragment,
) => {
  const itemGroupsEnabled = useDynamicConfigValue('item_groups_enabled');
  const brandGTINFilter = useGTINMetadataFilter({
    extractor: brandExtractor,
    includeFilters,
    key: CatalogFilter.BRAND,
    label: i18n.t('product_spec.brand_name'),
    remoteOptions: remoteFilters?.list_filters?.brand_names,
    vpsWithMetadata,
  });

  const freeFromGTINFilter = useGTINMetadataFilter({
    extractor: freeFromExtractor,
    includeFilters,
    key: CatalogFilter.FREE_FROM,
    label: i18n.t('free_from'),
    remoteOptions: remoteFilters?.list_filters?.free_from_allergens,
    translate: getAllergenTranslation,
    vpsWithMetadata,
  });

  const packSizeGTINFilter = useGTINMetadataFilter({
    extractor: packSizeExtractor,
    includeFilters,
    key: CatalogFilter.PACK_SIZE,
    label: i18n.t('pack_size'),
    remoteOptions: remoteFilters?.list_filters?.pack_sizes,
    vpsWithMetadata,
  });

  const originCountryGTINFilter = useGTINMetadataFilter({
    extractor: originCountryExtractor,
    includeFilters,
    key: CatalogFilter.COUNTRY,
    label: i18n.t('product_spec.origin_country'),
    remoteOptions: remoteFilters?.list_filters?.origin_countries,
    vpsWithMetadata,
  });

  const kosherGTINFilter = useGTINBooleanFilter({
    extractor: (metadata) => Boolean(metadata.kosher),
    includeFilters,
    isAvailable: remoteFilters?.flag_filters?.includes(
      GtinFilterAvailabilityFlags.Kosher,
    ),
    key: CatalogFilter.KOSHER,
    label: i18n.t('product_spec.kosher'),
    vpsWithMetadata,
  });

  const childNutritionGTINFilter = useGTINBooleanFilter({
    extractor: (metadata) => Boolean(metadata.child_nutrition),
    includeFilters,
    isAvailable: remoteFilters?.flag_filters?.includes(
      GtinFilterAvailabilityFlags.ChildNutritionLabel,
    ),
    key: CatalogFilter.CHILD_NUTRITION,
    label: i18n.t('product_spec.child_nutrition'),
    vpsWithMetadata,
  });

  const categoryGTINFilter = useGTINMetadataFilter({
    extractor: categoryExtractor,
    includeFilters: itemGroupsEnabled ? false : includeFilters,
    key: CatalogFilter.SUBCATEGORY,
    label: i18n.t('subcategory'),
    remoteOptions: remoteFilters?.list_filters?.subcategories,
    vpsWithMetadata,
  });

  return useMemo(() => {
    if (!includeFilters) {
      return {
        gtinFilters: {
          applyAllStagedFilters: () => {},
          flagFilters: [],
          listFilters: [],
          resetAllStagedFilters: () => {},
          resetFilters: () => {},
          stageAllFilters: () => {},
        },
        matchingVariantPackUUIDs: vpsWithMetadata.reduce<Set<string>>(
          (acc, vp) => {
            acc.add(vp.uuid);
            return acc;
          },
          new Set(),
        ),
      };
    }
    const listFilters = [
      categoryGTINFilter,
      brandGTINFilter,
      freeFromGTINFilter,
      packSizeGTINFilter,
      originCountryGTINFilter,
    ];
    const flagFilters = [kosherGTINFilter, childNutritionGTINFilter];

    const matchingVariantPackUUIDs = vpsWithMetadata.reduce<Set<string>>(
      (acc, vp) => {
        if (
          listFilters.every((filter) => filter.filterMatches(vp)) &&
          flagFilters.every((filter) => filter.filterMatches(vp))
        ) {
          acc.add(vp.uuid);
        }
        return acc;
      },
      new Set(),
    );

    const gtinFilters: GTINFilters = {
      applyAllStagedFilters: () => {
        for (const listFilter of listFilters) {
          listFilter.applyStage();
        }
        for (const flagFilter of flagFilters) {
          flagFilter.applyStage();
        }
      },
      flagFilters,
      listFilters,
      resetAllStagedFilters: () => {
        for (const listFilter of listFilters) {
          listFilter.resetStage();
        }
        for (const flagFilter of flagFilters) {
          flagFilter.resetStage();
        }
      },
      resetFilters: () => {
        for (const listFilter of listFilters) {
          listFilter.updateFilter(new Set());
        }
        for (const flagFilter of flagFilters) {
          flagFilter.updateFilter(false);
        }
      },
      stageAllFilters: () => {
        for (const listFilter of listFilters) {
          listFilter.stageValue();
        }
        for (const flagFilter of flagFilters) {
          flagFilter.stageValue();
        }
      },
    };
    return {
      gtinFilters,
      matchingVariantPackUUIDs,
    };
  }, [
    brandGTINFilter,
    childNutritionGTINFilter,
    freeFromGTINFilter,
    includeFilters,
    kosherGTINFilter,
    originCountryGTINFilter,
    packSizeGTINFilter,
    categoryGTINFilter,
    vpsWithMetadata,
  ]);
};
